import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

// Property type definition to match estatebud API
interface Property {
  id: string;
  title?: {
    'en-US': string;
  };
  description?: {
    'en-US': string;
  };
  data?: {
    bedrooms?: string;
    bathrooms?: string;
    garages?: string;
    lotSize?: string;
    stories?: string;
    yearBuilt?: string;
  };
  street?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  price?: number;
  images?: Array<{
    medium: string;
    caption: string;
    status: number;
  }>;
  floorplans?: Array<{
    medium: string;
    caption: string;
    status: number;
  }>;
  labels?: Array<{
    id: string;
    name: string;
    color: string;
    public: boolean;
  }>;
  agents?: Array<{
    firstname: string;
    lastname: string;
    title: string;
    email: string;
    phone?: string;
    photo?: string;
  }>;
}

const PropertyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);

  useEffect(() => {
    const fetchPropertyDetails = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await axios.get(`/api/properties/${id}`);
        setProperty(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching property details:', err);
        setError('Failed to load property details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyDetails();
  }, [id]);

  const getFloorPlanModelName = () => {
    if (!property?.labels) return null;
    
    const homeModels = ['Aurora', 'Victoria', 'Diana', 'Ceres', 'Pomona', 'Terra', 'Vesta', 'Cybele'];
    const modelLabel = property.labels.find(label => homeModels.includes(label.name));
    return modelLabel ? modelLabel.name : null;
  };

  const getStatusLabel = () => {
    if (!property?.labels) return null;
    
    if (property.labels.some(label => label.name === 'SOLD')) {
      return { text: 'SOLD', color: 'bg-red-600' };
    } else if (property.labels.some(label => label.name === 'UNDER CONTRACT')) {
      return { text: 'UNDER CONTRACT', color: 'bg-red-600' };
    } else if (property.labels.some(label => label.name.startsWith('Move in'))) {
      const moveInLabel = property.labels.find(label => label.name.startsWith('Move in'));
      return { text: moveInLabel?.name || '', color: 'bg-green-600' };
    }
    
    return null;
  };
  
  if (loading) {
    return (
      <div className="container-custom py-12 md:py-24 text-center">
        <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-lg text-gray-600">Loading property details...</p>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="container-custom py-12 md:py-24 text-center">
        <h1 className="mb-8">Property Not Found</h1>
        <p className="text-lg text-gray-700 mb-8">
          {error || 'The property you are looking for could not be found.'}
        </p>
        <Link to="/neighborhoods/highlands" className="btn btn-primary">
          Back to The Highlands
        </Link>
      </div>
    );
  }

  const statusLabel = getStatusLabel();
  const floorPlanModel = getFloorPlanModelName();
  const mainImage = property.images && property.images.length > 0 ? property.images[0].medium : null;

  return (
    <div>
      {/* Hero Section */}
      <div className="relative h-80 md:h-96 lg:h-[500px] overflow-hidden">
        {mainImage ? (
          <img 
            src={mainImage} 
            alt={property.title?.['en-US'] || 'Property'} 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <p className="text-gray-500">No image available</p>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 text-white">
          <div className="container-custom">
            <h1 className="drop-shadow-sm mb-2">{property.title?.['en-US'] || `Property #${property.id}`}</h1>
            <p className="text-xl text-white/90 drop-shadow-sm">
              {property.street}{property.city ? `, ${property.city}` : ''}
              {property.state ? `, ${property.state}` : ''}
              {property.zipcode ? ` ${property.zipcode}` : ''}
            </p>
          </div>
        </div>
        {statusLabel && (
          <div className={`absolute top-6 right-6 ${statusLabel.color} text-white px-4 py-2 rounded-md text-sm font-semibold`}>
            {statusLabel.text}
          </div>
        )}
      </div>

      <div className="container-custom py-12 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            {/* Property Images Gallery */}
            {property.images && property.images.length > 1 && (
              <div className="mb-12">
                <h2 className="text-2xl font-serif mb-6">Property Photos</h2>
                
                <div className="mb-8">
                  <img 
                    src={property.images[activeImageIndex].medium} 
                    alt={property.images[activeImageIndex].caption || `Property view ${activeImageIndex + 1}`}
                    className="w-full h-auto object-contain rounded-lg shadow-md"
                  />
                </div>
                
                <div className="flex space-x-3 overflow-x-auto pb-4 scrollbar-hide">
                  {property.images.map((image, index) => (
                    <button
                      key={index}
                      className={`w-16 h-16 sm:w-20 sm:h-20 rounded-md overflow-hidden border-2 transition flex-shrink-0 ${
                        index === activeImageIndex ? 'border-primary' : 'border-transparent'
                      }`}
                      onClick={() => setActiveImageIndex(index)}
                    >
                      <img 
                        src={image.medium} 
                        alt={image.caption || `Property ${index + 1}`} 
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Property Description */}
            <div className="mb-12">
              <h2 className="text-2xl font-serif mb-4">About This Property</h2>
              <p className="text-lg text-gray-700 mb-8">
                {property.description?.['en-US'] || 'No description available.'}
              </p>
            </div>
            
            {/* Property Details */}
            <div className="mb-12">
              <h2 className="text-2xl font-serif mb-6">Property Details</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {property.data?.bedrooms && (
                  <div>
                    <h3 className="text-gray-500 text-sm">Bedrooms</h3>
                    <p className="text-lg">{property.data.bedrooms}</p>
                  </div>
                )}
                {property.data?.bathrooms && (
                  <div>
                    <h3 className="text-gray-500 text-sm">Bathrooms</h3>
                    <p className="text-lg">{property.data.bathrooms}</p>
                  </div>
                )}
                {property.data?.garages && (
                  <div>
                    <h3 className="text-gray-500 text-sm">Garage</h3>
                    <p className="text-lg">{property.data.garages} Car</p>
                  </div>
                )}
                {property.data?.lotSize && (
                  <div>
                    <h3 className="text-gray-500 text-sm">Lot Size</h3>
                    <p className="text-lg">{property.data.lotSize}</p>
                  </div>
                )}
                {property.data?.stories && (
                  <div>
                    <h3 className="text-gray-500 text-sm">Stories</h3>
                    <p className="text-lg">{property.data.stories}</p>
                  </div>
                )}
                {property.data?.yearBuilt && (
                  <div>
                    <h3 className="text-gray-500 text-sm">Year Built</h3>
                    <p className="text-lg">{property.data.yearBuilt}</p>
                  </div>
                )}
                {property.price && (
                  <div>
                    <h3 className="text-gray-500 text-sm">Price</h3>
                    <p className="text-lg text-primary font-semibold">${property.price.toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Floor Plans */}
            {property.floorplans && property.floorplans.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-serif mb-6">Floor Plans</h2>
                <div className="space-y-8">
                  {property.floorplans.map((floorplan, index) => (
                    <div key={index} className="border-t border-gray-200 pt-6 first:border-0 first:pt-0">
                      <h3 className="font-medium mb-3">{floorplan.caption || `Floor Plan ${index + 1}`}</h3>
                      <div className="mb-4">
                        <img 
                          src={floorplan.medium} 
                          alt={floorplan.caption || `Floor Plan ${index + 1}`}
                          className="w-full h-auto object-contain rounded-lg shadow-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Floor Plan Model */}
            {floorPlanModel && (
              <div className="mb-12">
                <h2 className="text-2xl font-serif mb-4">Home Model</h2>
                <p className="mb-4">
                  This property features our {floorPlanModel} home model.
                </p>
                <Link 
                  to={`/homes/${floorPlanModel.toLowerCase()}`} 
                  className="btn btn-outline"
                >
                  View {floorPlanModel} Home Details
                </Link>
              </div>
            )}
          </div>
          
          <div className="lg:col-span-1">
            <div className="bg-primary/5 rounded-lg p-6 shadow-sm sticky top-24">
              <h3 className="text-xl font-serif mb-4">Property Details</h3>
              <div className="space-y-4">
                {property.street && (
                  <div>
                    <div className="text-sm text-gray-500">Address</div>
                    <div className="font-medium">
                      {property.street}
                      {property.city ? <><br />{property.city}</> : ''}
                      {property.state && property.zipcode ? `, ${property.state} ${property.zipcode}` : ''}
                      {property.state && !property.zipcode ? `, ${property.state}` : ''}
                      {!property.state && property.zipcode ? ` ${property.zipcode}` : ''}
                    </div>
                  </div>
                )}
                
                {property.data?.bedrooms && (
                  <div>
                    <div className="text-sm text-gray-500">Bedrooms</div>
                    <div className="font-medium">{property.data.bedrooms}</div>
                  </div>
                )}
                
                {property.data?.bathrooms && (
                  <div>
                    <div className="text-sm text-gray-500">Bathrooms</div>
                    <div className="font-medium">{property.data.bathrooms}</div>
                  </div>
                )}
                
                {floorPlanModel && (
                  <div>
                    <div className="text-sm text-gray-500">Home Model</div>
                    <div className="font-medium">{floorPlanModel}</div>
                  </div>
                )}
                
                {statusLabel && (
                  <div>
                    <div className="text-sm text-gray-500">Status</div>
                    <div className={`font-medium ${statusLabel.color === 'bg-red-600' ? 'text-red-600' : 'text-green-600'}`}>
                      {statusLabel.text}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-8">
                <Link 
                  to="/contact" 
                  className="btn btn-primary w-full mb-3"
                >
                  Contact Us About This Property
                </Link>
                
                <Link 
                  to="/neighborhoods/highlands" 
                  className="btn btn-outline w-full mb-3"
                >
                  Back to The Highlands
                </Link>
                
                {floorPlanModel && (
                  <Link 
                    to={`/homes/${floorPlanModel.toLowerCase()}`} 
                    className="btn btn-outline w-full"
                  >
                    View {floorPlanModel} Floor Plan
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail; 